import pino from 'pino';
import { Transport } from '../types/Transport';
import { LogEntry } from '../types/LogEntry';
import { TransportConfig } from '../types/TransportConfig';
import { LogLevel } from '../types/LogLevel';

/**
 * @group Classes
 * 
 * @summary
 * <blockquote>Console transport for logging to stdout/stderr with color formatting</blockquote>
 * 
 * @example
 * ```typescript
 * const transport = new ConsoleTransport({ level: 'info', categories: [] });
 * await transport.log({ timestamp: '2024-01-01T00:00:00Z', level: 'info', category: 'users.signin', message: 'User signed in' });
 * ```
 */
type PinoLogMethod = (obj: object, msg?: string, ...args: any[]) => void;

const isNode = typeof window === 'undefined';

const COLORS = {
  info: '\x1b[36m',    // cyan
  warn: '\x1b[33m',    // yellow
  error: '\x1b[31m',   // red
  debug: '\x1b[90m',   // gray
  reset: '\x1b[0m',
};

function getCircularReplacer() {
  const seen = new WeakSet();
  return (key: string, value: any) => {
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) {
        return "[Circular]";
      }
      seen.add(value);
    }
    return value;
  };
}

function prettyPrint(level: LogLevel, msg: string, obj?: object) {
  const color = COLORS[level] || COLORS.info;
  const time = new Date().toISOString();
  let output = `${color}[${level.toUpperCase()}]${COLORS.reset} ${time} ${msg}`;
  if (obj) {
    // If error, print stack nicely
    if ('err' in obj && obj.err instanceof Error) {
      output += `\n${color}${obj.err.stack}${COLORS.reset}`;
    } else if ('err' in obj && obj.err && typeof obj.err === 'object' && 'stack' in obj.err) {
      output += `\n${color}${String(obj.err.stack)}${COLORS.reset}`;
    }
    // Print context (excluding error)
    const { err, ...rest } = obj as any;
    if (Object.keys(rest).length > 0) {
      try {
        // Filter out undefined values and empty objects
        const filteredRest = Object.fromEntries(
          Object.entries(rest).filter(([_, value]) => 
            value !== undefined && 
            value !== null && 
            !(typeof value === 'object' && Object.keys(value).length === 0)
          )
        );
        
        if (Object.keys(filteredRest).length > 0) {
          output += `\n${COLORS.reset}${JSON.stringify(filteredRest, getCircularReplacer(), 2)}`;
        }
      } catch (error) {
        output += `\n${COLORS.reset}[Object with circular references]`;
      }
    }
  }
  return output;
}

export class ConsoleTransport implements Transport {
  private logger: pino.Logger;

  constructor(private config: TransportConfig) {
    if (isNode) {
      // Dummy pino instance for type compatibility
      this.logger = pino({ level: config.level, enabled: false });
    } else {
      this.logger = pino({
        level: config.level,
        browser: {
          write: {
            info: (obj: object, msg?: string) => {
              console.log(`%c${msg || ''}`, 'color: #00a8ff', obj);
            },
            warn: (obj: object, msg?: string) => {
              console.warn(`%c${msg || ''}`, 'color: #fbc531', obj);
            },
            error: (obj: object, msg?: string) => {
              let errorObj = obj;
              if (obj && 'err' in obj) {
                const error = obj.err;
                if (error instanceof Error) {
                  const cleanStack = this.cleanStack(error.stack || '');
                  const cleanError = new Error(error.message);
                  cleanError.stack = cleanStack;
                  errorObj = { ...obj, err: cleanError };
                } else if (typeof error === 'object' && error !== null) {
                  const message = 'message' in error ? String(error.message) : 'Unknown error';
                  const stack = 'stack' in error ? this.cleanStack(String(error.stack)) : '';
                  const cleanError = new Error(message);
                  cleanError.stack = stack;
                  errorObj = { ...obj, err: cleanError };
                }
              }
              console.error(`%c${msg || ''}`, 'color: #e84118', errorObj);
            },
            debug: (obj: object, msg?: string) => {
              console.debug(`%c${msg || ''}`, 'color: #718093', obj);
            },
          },
        },
        timestamp: () => `,"time":"${new Date().toISOString()}"`,
      });
    }
  }

  private cleanStack(stack: string): string {
    if (!stack) return '';
    return stack
      .split('\n')
      .filter(line => {
        const isSourceCode = line.includes('/app/') || 
          line.includes('/components/') || 
          line.includes('/lib/') ||
          line.includes('/utils/');
        const isFirstLine = !line.includes('at ');
        const isNextInternal = line.includes('next/dist/') || 
          line.includes('node_modules/') ||
          line.includes('(rsc)/') ||
          line.includes('(app-ssr)/');
        return isFirstLine || (isSourceCode && !isNextInternal);
      })
      .join('\n');
  }

  async log(entry: LogEntry): Promise<void> {
    if (!this.shouldLog(entry)) return;
    const message = entry.message;
    const context = entry.context || {};
    const resourceContext = entry.resourceContext || {};
    
    // Merge context and resourceContext
    const mergedContext = { ...context, ...resourceContext };
    
    if (isNode) {
      // Use custom pretty print in Node.js
      if (entry.error) {
        console.error(prettyPrint(entry.level, message, { err: entry.error, ...mergedContext }));
      } else {
        const logFn = entry.level === 'warn' ? console.warn : entry.level === 'debug' ? console.debug : console.log;
        logFn(prettyPrint(entry.level, message, mergedContext));
      }
    } else {
      const logMethod = this.getLogMethod(entry.level);
      if (entry.error) {
        logMethod({ err: entry.error, ...mergedContext }, message);
      } else {
        logMethod(mergedContext, message);
      }
    }
  }

  async close(): Promise<void> {
    // No cleanup needed for console transport
  }

  private shouldLog(entry: LogEntry): boolean {
    const levelPriority = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3,
    };
    // Only check log level - category/domain filtering is handled at the Logger level
    return levelPriority[entry.level] >= levelPriority[this.config.level];
  }

  private getLogMethod(level: LogLevel): PinoLogMethod {
    switch (level) {
      case 'error':
        return this.logger.error.bind(this.logger);
      case 'warn':
        return this.logger.warn.bind(this.logger);
      case 'info':
        return this.logger.info.bind(this.logger);
      case 'debug':
        return this.logger.debug.bind(this.logger);
      default:
        return this.logger.info.bind(this.logger);
    }
  }
} 